'use client';

import {
// ArrowDownOutlined, ArrowUpOutlined,
DeleteOutlined } from '@ant-design/icons';
import { useSuspenseQuery } from '@tanstack/react-query';
// import {PriceSelect} from '../PriceSelect/PriceSelect';
import { Switch } from 'antd';
import { AnimatePresence, motion } from 'framer-motion';
import { useRouter, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounceValue } from 'usehooks-ts';
import { actionTypesOptions } from '@/entities/Actions/api/actionTypes';
import { celebritiesListOptions } from '@/entities/Celebrity/api/celebritiesList';
import { getQueryClient } from '@/libs/queryClient';
import { FilterTags } from '@/shared/components/FilterTags';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { CelebritySearchSelect } from '../CelebritySearchSelect';
import { type AppliedFilter, AppliedFilters } from './AppliedFilters';
interface FilterActionsProps {
  onFilterChange?: (filters: FilterValues) => void;
  initialFilters?: Partial<FilterValues>;
  className?: string;
  /**
   * Цвет фона для всех селектов
   * @default #392f99
   */
  selectBackgroundColor?: string;
}
export interface FilterValues {
  celebrities?: number[];
  price?: [number, number];
  celebrityName?: string;
  sortBy?: 'coinsPrice' | undefined;
  sortType?: 'asc' | 'desc' | undefined;
  type?: string;
  page?: number;
}
export const FilterActions: FC<FilterActionsProps> = ({
  className = ''
}) => {
  const t = useTranslations('experiences.filters');
  const router = useRouter();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [isOpen, setIsOpen] = useState(false);

  // Блокировка прокрутки страницы при открытом меню фильтров
  useEffect(() => {
    if (isMobile && isOpen) {
      // Сохраняем текущую позицию прокрутки
      const scrollY = window.scrollY;
      // Блокируем прокрутку
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    } else if (isMobile) {
      // Восстанавливаем прокрутку
      const scrollY_0 = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      // Восстанавливаем позицию прокрутки
      if (scrollY_0) {
        window.scrollTo(0, parseInt(scrollY_0 || '0') * -1);
      }
    }
  }, [isMobile, isOpen]);
  const searchParams = useSearchParams();
  const {
    data: actionTypes = []
  } = useSuspenseQuery(actionTypesOptions());
  const tagsTypes = useMemo(() => ['all', 'poll', 'quiz', 'challenge', 'platform'], [actionTypes]);
  const tags = useMemo(() => [t('all'), t('poll'), t('quiz'), t('challenge'), t('platform')], [actionTypes]);
  const initialFilters = useMemo<Partial<FilterValues>>(() => {
    return {
      type: searchParams.get('type') ?? undefined,
      celebrities: searchParams.get('type') !== 'platform' ? searchParams.get('celebrities') ? searchParams.get('celebrities')?.split(',').map(Number) : undefined : undefined,
      celebrityName: searchParams.get('celebrityName') ?? undefined,
      price: searchParams.get('price') ? searchParams.get('price')?.split(',').map(Number) as [number, number] : undefined,
      // sortBy: searchParams.get('sortBy') as 'coinsPrice' | undefined,
      // sortType: searchParams.get('sortType') as 'asc' | 'desc' | undefined,
      page: searchParams.get('page') ? Number(searchParams.get('page')) : undefined
    };
  }, [searchParams]);
  const [filters, setFilters] = useState<FilterValues>({
    type: initialFilters?.type ?? undefined,
    celebrities: initialFilters.type !== 'platform' ? initialFilters?.celebrities ?? undefined : undefined,
    price: initialFilters?.price ?? undefined,
    celebrityName: initialFilters?.celebrityName ?? undefined,
    // sortBy: initialFilters?.sortBy ?? undefined,
    // sortType: initialFilters?.sortType ?? undefined,
    page: initialFilters?.page ?? undefined
  });
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      type: initialFilters?.type ?? undefined,
      celebrities: initialFilters.type !== 'platform' ? initialFilters?.celebrities ?? undefined : undefined,
      price: initialFilters?.price ?? undefined,
      // sortBy: initialFilters?.sortBy ?? undefined,
      // sortType: initialFilters?.sortType ?? undefined,
      page: initialFilters?.page ?? undefined
    }));
  }, [initialFilters]);
  const [localFilters, setLocalFilters] = useState<FilterValues>({
    type: initialFilters?.type ?? undefined,
    celebrities: initialFilters.type !== 'platform' ? initialFilters?.celebrities ?? undefined : undefined,
    price: initialFilters?.price ?? undefined,
    celebrityName: initialFilters?.celebrityName ?? undefined,
    // sortBy: initialFilters?.sortBy ?? undefined,
    // sortType: initialFilters?.sortType ?? undefined,
    page: initialFilters?.page ?? undefined
  });
  const [debouncedFilters] = useDebounceValue(filters, 500);
  const stableDebouncedFilters = useMemo(() => debouncedFilters ? JSON.stringify(debouncedFilters) : '', [debouncedFilters]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: because of debouncedFilters
  useEffect(() => {
    const params = new URLSearchParams();
    Object.entries(debouncedFilters).forEach(([key, value]) => {
      if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }
    });
    router.push(`?${params.toString()}`);
  }, [stableDebouncedFilters]);
  const handleFilterChange = (key_0: keyof FilterValues, value_0: any) => {
    setFilters(prev_0 => ({
      ...prev_0,
      page: 1,
      [key_0]: value_0
    }));
  };
  const handleLocalFilterChange = (key_1: keyof FilterValues, value_1: any) => {
    setLocalFilters(prev_1 => ({
      ...prev_1,
      [key_1]: value_1
    }));
  };
  const {
    data: celebrities = []
  } = useSuspenseQuery(celebritiesListOptions({}));
  const getCelebrityShortNameById = useCallback((id: number) => {
    const celebrity_0 = celebrities?.find(celebrity => celebrity.id === id);
    return `${celebrity_0?.first_name} ${celebrity_0?.last_name}`;
  }, [celebrities]);
  const getExperienceTypeNameById = useCallback((id_0: number) => {
    return id_0.toString();
  }, []);
  const appliedFilters = useMemo(() => {
    return Object.entries(filters).flatMap(([key_2, value_2]) => {
      if (value_2) {
        let type: string | undefined;
        let mapper: ((id: number) => string | undefined) | undefined;
        switch (key_2) {
          case 'celebrities':
            // type = 'creators';
            mapper = getCelebrityShortNameById;
            break;
          case 'types':
            type = 'type';
            mapper = getExperienceTypeNameById;
            break;
          case 'tags':
            type = 'tag';
            break;
          default:
            return;
        }
        return Array.isArray(value_2) ? value_2.map(item => ({
          type,
          key: key_2,
          value: item,
          label: mapper ? mapper(item) : item
        })) : {
          type,
          key: key_2,
          value: value_2,
          label: mapper ? mapper(value_2) : value_2
        };
      }
    }).filter(Boolean) as AppliedFilter[];
  }, [filters, getCelebrityShortNameById, getExperienceTypeNameById]);
  const localAppliedFilters = useMemo(() => {
    return Object.entries(localFilters).flatMap(([key_3, value_3]) => {
      if (value_3) {
        let type_0: string | undefined;
        let mapper_0: ((id: number) => string | undefined) | undefined;
        switch (key_3) {
          case 'celebrities':
            // type = 'creators';
            mapper_0 = getCelebrityShortNameById;
            break;
          case 'types':
            type_0 = 'type';
            mapper_0 = getExperienceTypeNameById;
            break;
          case 'tags':
            type_0 = 'tag';
            break;
          default:
            return;
        }
        return Array.isArray(value_3) ? value_3.map(item_0 => ({
          type: type_0,
          key: key_3,
          value: item_0,
          label: mapper_0 ? mapper_0(item_0) : item_0
        })) : {
          type: type_0,
          key: key_3,
          value: value_3,
          label: mapper_0 ? mapper_0(value_3) : value_3
        };
      }
    }).filter(Boolean) as AppliedFilter[];
  }, [localFilters, getCelebrityShortNameById, getExperienceTypeNameById]);
  const renderDesktopFilter = () => <>
      <FilterTags tags={tags} selectedTag={initialFilters.type && tags[tagsTypes.indexOf(initialFilters.type)]} onTagSelect={tag => handleFilterChange('type', tagsTypes[tags.indexOf(tag)])} />
      <div className={`mt-7 flex w-full flex-col gap-4 ${className}`}>
        {/* Filter List */}
        <div className="flex gap-4">
          {/* Celebrities */}
          {initialFilters.type !== 'platform' && <CelebritySearchSelect value={filters.celebrities} onChange={value_4 => handleFilterChange('celebrities', value_4)} placeholder={t('filter.celebrities')} className="flex-1 [&+button]:flex-none" searchValue={filters.celebrityName ?? ''} setSearchValue={value_5 => handleFilterChange('celebrityName', value_5)} />}

          {/* Price */}
          {/* <PriceSelect
            className="flex-1"
            value={filters.price}
            onChange={value => handleFilterChange('price', value)}
            placeholder={t('filter.price')}
            currency="COINS"
            min={0}
            max={100}
            step={1}
            defaultValue={[0, 50]}
           /> */}

          {initialFilters.type !== 'platform' && <button type="button" className="ml-2 flex flex-1 items-center justify-center gap-2 pl-3 text-base text-[#A0A3E2]" onClick={() => {
          handleFilterChange('celebrities', []);
          handleFilterChange('celebrityName', undefined);
          handleFilterChange('type', undefined);
          handleFilterChange('sortBy', undefined);
          handleFilterChange('sortType', undefined);
          handleFilterChange('price', undefined);
        }}>
              <DeleteOutlined className="size-4" />
              <span className="text-base">{t('filter.removeAllFilters')}</span>
            </button>}
        </div>

        <div className="mb-9 flex items-center gap-11">
          <div className="flex items-center gap-3">
            <Switch className="!h-5 min-w-10 [&.ant-switch-checked_.ant-switch-handle]:!left-[calc(100%-20px)] [&_.ant-switch-handle]:!left-0 [&_.ant-switch-handle]:!top-0 [&_.ant-switch-handle]:!size-5" checked={filters.price?.at(0) === 0 && filters.price?.at(1) === 0} onChange={value_6 => handleFilterChange('price', value_6 ? [0, 0] : undefined)} />
            <span className={`text-base leading-none text-[#A0A3E2] ${filters.price?.at(0) === 0 && filters.price?.at(1) === 0 ? 'text-white' : ''}`}>
              {t('freeActionsOnly')}
            </span>
          </div>
          {/* Results and Sorting */}
          {/* <div className="flex h-10 shrink-0 items-center whitespace-nowrap">
            <div className="flex items-center px-1">
              <span className="text-base text-[#A0A3E2]">{t('sortBy')}</span>
            </div>
            <div className="flex items-center gap-6">
              {['coinsPrice'].map(item => (
                <button
                  onClick={() => {
                    if (filters.sortBy === item) {
                      handleFilterChange(
                        'sortType',
                        !filters.sortType ? 'asc' : filters.sortType === 'desc' ? null : 'desc',
                      );
                    } else {
                      handleFilterChange('sortBy', item);
                      handleFilterChange('sortType', 'asc');
                    }
                  }}
                  key={item}
                  type="button"
                  className={`flex items-center gap-1 px-1 text-base font-semibold leading-[1.4]`}
                  style={{
                    color: filters.sortBy === item && filters.sortType ? '#2a72fd' : '#A0A3E2',
                  }}
                >
                  <div className="flex size-4 items-center justify-center">
                    <ArrowUpOutlined
                      className="-mr-0.5 -mt-1 size-3"
                      style={{color: filters.sortType === 'asc' ? '#2a72fd' : '#A0A3E2'}}
                    />
                    <ArrowDownOutlined
                      className="-mb-1 -ml-0.5 size-3"
                      style={{color: filters.sortType === 'desc' ? '#2a72fd' : '#A0A3E2'}}
                    />
                  </div>
                  {t(item as any)}
                </button>
              ))}
            </div>
           </div> */}

          {/* Applied Filters */}
          <AppliedFilters className="shrink-1" filters={appliedFilters} onRemove={({
          key: key_4,
          value: value_7
        }) => handleFilterChange(key_4 as keyof FilterValues, filters[key_4 as keyof Pick<FilterValues, 'celebrities'>]?.filter(item_1 => item_1 !== value_7))} />
        </div>
      </div>
    </>;
  const renderMobileFilter = () => <>
      <FilterTags tags={tags} selectedTag={initialFilters.type && tags[tagsTypes.indexOf(initialFilters.type)]} onTagSelect={tag_0 => handleFilterChange('type', tagsTypes[tags.indexOf(tag_0)])} />
      {/* Header */}
      <div className="mb-4 mt-2 flex flex-row items-center justify-between gap-4">
        {initialFilters.type !== 'platform' && <button type="button" onClick={() => setIsOpen(!isOpen)} className="flex h-10 items-center gap-2 text-sm text-[#A0A3E2]">
            <img src="/icons/filter.svg" alt="Filter" className="size-4" />
            {t('title')}
          </button>}

        {/* Results and Sorting */}
        {/* <div className="flex h-10 shrink-0 items-center whitespace-nowrap">
          <div className="flex items-center gap-6">
            {['coinsPrice'].map(item => (
              <button
                onClick={() => {
                  if (filters.sortBy === item) {
                    handleFilterChange(
                      'sortType',
                      !filters.sortType ? 'asc' : filters.sortType === 'desc' ? null : 'desc',
                    );
                  } else {
                    handleFilterChange('sortBy', item);
                    handleFilterChange('sortType', 'asc');
                  }
                }}
                key={item}
                type="button"
                className={`flex items-center gap-1 text-sm font-semibold leading-[1.4]`}
                style={{
                  color: filters.sortBy === item && filters.sortType ? '#2a72fd' : '#A0A3E2',
                }}
              >
                <div className="flex size-4 items-center justify-center">
                  <ArrowUpOutlined
                    className="-mr-0.5 -mt-1 size-3"
                    style={{color: filters.sortType === 'asc' ? '#2a72fd' : '#A0A3E2'}}
                  />
                  <ArrowDownOutlined
                    className="-mb-1 -ml-0.5 size-3"
                    style={{color: filters.sortType === 'desc' ? '#2a72fd' : '#A0A3E2'}}
                  />
                </div>
                {t(item as any)}
              </button>
            ))}
          </div>
         </div> */}
        <div className="flex h-10 items-center gap-2">
          <Switch className="!h-4 !min-w-8 [&.ant-switch-checked_.ant-switch-handle]:!left-[calc(100%-16px)] [&_.ant-switch-handle]:!top-0 [&_.ant-switch-handle]:left-0 [&_.ant-switch-handle]:!size-4 [&_.ant-switch-inner]:!p-0" checked={filters.price?.at(0) === 0 && filters.price?.at(1) === 0} onChange={value_8 => handleFilterChange('price', value_8 ? [0, 0] : undefined)} />
          <span className={`text-sm font-medium leading-none text-[#A0A3E2] ${filters.price?.at(0) === 0 && filters.price?.at(1) === 0 ? 'text-white' : ''}`}>
            {t('freeActionsOnly')}
          </span>
        </div>
      </div>
      {/* Mobile Filter Panel */}
      <AnimatePresence>
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: '100%'
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: '100%'
      }} transition={{
        duration: 0.3
      }} className="fixed bottom-0 left-0 right-0 top-[32px] z-[9997] flex flex-col gap-2 bg-[#100D32] p-4">
            <div className="flex h-full flex-col justify-between">
              <div className="flex flex-col grow shrink overflow-hidden">
                {/* Title */}
                <div className="mb-6 flex items-center justify-between">
                  <button type="button" className="flex h-10 items-center gap-3 px-2 font-['Montserrat'] text-xl font-semibold text-white">
                    <img src="/icons/filter.svg" alt="Filter" className="size-6" />
                    {t('title')}
                  </button>

                  <button type="button" className="flex items-center gap-2 pl-3 pr-2 text-base text-[#A0A3E2]" onClick={() => {
                handleLocalFilterChange('celebrities', []);
                handleLocalFilterChange('price', undefined);
              }}>
                    <DeleteOutlined className="size-4" />
                    <span className="text-base">{t('filter.removeAllFilters')}</span>
                  </button>
                </div>

                {/* Filter List */}
                <div className="mb-4 flex flex-col gap-3">
                  {/* Price */}
                  {/* <PriceSelect
                    open={true}
                    className="mb-[148px]"
                    value={localFilters.price}
                    onChange={value => handleLocalFilterChange('price', value)}
                    placeholder={t('filter.price')}
                    currency="COINS"
                    min={0}
                    max={100}
                    step={1}
                    defaultValue={[0, 50]}
                    autoAdjustOverflow={false}
                   /> */}

                  {/* Celebrities */}
                  <CelebritySearchSelect value={localFilters.celebrities} onChange={value_9 => handleLocalFilterChange('celebrities', value_9)} placeholder={t('filter.celebrities')} className="w-full" searchValue={localFilters.celebrityName ?? ''} setSearchValue={value_10 => handleLocalFilterChange('celebrityName', value_10)} />
                </div>

                {/* Applied Filters */}
                <AppliedFilters filters={localAppliedFilters} onRemove={filter => handleLocalFilterChange(filter.key as keyof FilterValues, localFilters[filter.key as keyof Pick<FilterValues, 'celebrities'>]?.filter(item_2 => item_2 !== filter.value))} />
              </div>

              {/* Bottom Buttons */}
              <div className="mt-4 flex flex-row gap-2">
                <button type="button" onClick={() => {
              handleLocalFilterChange('celebrities', filters.celebrities);
              handleLocalFilterChange('celebrityName', filters.celebrityName ?? '');
              handleLocalFilterChange('price', filters.price);
              setIsOpen(false);
            }} className="h-[36px] grow rounded-lg border-2 border-white/10 font-['Montserrat'] text-xs font-bold uppercase text-white">
                  {t('filter.remove')}
                </button>
                <button type="button" onClick={() => {
              // Apply filters logic
              handleFilterChange('celebrities', localFilters.celebrities ?? []);
              handleFilterChange('celebrityName', localFilters.celebrityName ?? '');
              handleFilterChange('price', localFilters.price);
              setIsOpen(false);
            }} className="h-[36px] grow rounded-lg bg-white font-['Montserrat'] text-xs font-bold uppercase text-[#392f99]">
                  {t('filter.apply')}
                </button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </>;
  return isMobile ? renderMobileFilter() : renderDesktopFilter();
};